import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Pencil, Trash2, Eye, Send, Copy, Code, Wand2, Blocks, GitBranch, Check, X } from 'lucide-react';
import { EmailEditor } from '../components/EmailEditor';
import { EmailBlockBuilder } from '../components/EmailBlockBuilder';
const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'results', label: 'Results' },
  { value: 'booking', label: 'Booking' },
  { value: 'application', label: 'Application' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'general', label: 'General' },
];

const MERGE_FIELDS = [
  { field: 'first_name', description: 'User\'s first name' },
  { field: 'last_name', description: 'User\'s last name' },
  { field: 'email', description: 'User\'s email address' },
  { field: 'primary_fear', description: 'Primary edge/pressure from EDGE diagnostic' },
  { field: 'fear_intensity', description: 'Edge intensity level' },
  { field: 'fear_score', description: 'EDGE diagnostic score' },
  { field: 'archetype', description: 'Stated archetype from CORE diagnostic' },
  { field: 'secondary_archetype', description: 'Core archetype' },
  { field: 'dashboard_url', description: 'Link to user dashboard' },
  { field: 'results_url', description: 'Link to results page' },
  { field: 'booking_url', description: 'Link to booking page' },
  { field: 'application_url', description: 'Link to application page' },
  { field: 'fear_assessment_url', description: 'Link to EDGE diagnostic' },
  { field: 'archetype_assessment_url', description: 'Link to CORE diagnostic' },
];

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showTestSend, setShowTestSend] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewData, setPreviewData] = useState({ subject: '', htmlContent: '' });
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [editorMode, setEditorMode] = useState('visual'); // 'visual', 'code', or 'blocks'
  const [emailBlocks, setEmailBlocks] = useState([]);
  
  // A/B Testing / Version Management
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [versionName, setVersionName] = useState('');
  
  // Enhanced preview
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop', 'tablet', 'mobile'
  const [showVariablesPanel, setShowVariablesPanel] = useState(false);
  const [variableSearch, setVariableSearch] = useState('');
  
  const DEVICE_WIDTHS = {
    desktop: '100%',
    tablet: '768px',
    mobile: '375px'
  };
  
  const filteredMergeFields = MERGE_FIELDS.filter(f => 
    f.field.toLowerCase().includes(variableSearch.toLowerCase()) ||
    f.description.toLowerCase().includes(variableSearch.toLowerCase())
  );
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    subject: '',
    htmlContent: '',
    description: '',
    category: 'general',
  });

  useEffect(() => {
    fetchTemplates();
  }, [categoryFilter]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = categoryFilter !== 'all' ? { category: categoryFilter, _t: Date.now() } : { _t: Date.now() };
      const response = await axios.get('/api/admin/email-templates', { params });
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      slug: '',
      subject: '',
      htmlContent: getDefaultTemplate(),
      description: '',
      category: 'general',
    });
    setEmailBlocks([]);
    setVersions([]);
    setSelectedVersion(null);
    setShowEditor(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      slug: template.slug,
      subject: template.subject,
      htmlContent: template.htmlContent,
      description: template.description || '',
      category: template.category || 'general',
    });
    setEmailBlocks(template.blocks || []);
    setVersions(template.versions || []);
    setSelectedVersion(template.activeVersion || null);
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.slug || !formData.subject || !formData.htmlContent) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        subject: formData.subject,
        htmlContent: formData.htmlContent,
        description: formData.description,
        category: formData.category,
        blocks: emailBlocks,
        versions: versions,
        activeVersion: selectedVersion,
      };
      
      if (editingTemplate) {
        await axios.put(`/api/admin/email-templates/${editingTemplate.id}`, payload);
        toast.success('Template updated successfully');
      } else {
        await axios.post('/api/admin/email-templates', {
          ...payload,
          slug: formData.slug,
        });
        toast.success('Template created successfully');
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      await axios.delete(`/api/admin/email-templates/${template.id}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handlePreview = async (template) => {
    try {
      // Add cache-busting timestamp to ensure fresh data
      const response = await axios.post(`/api/admin/email-templates/${template.id}/preview?_t=${Date.now()}`, {
        toEmail: 'preview@example.com',
        mergeData: {},
      });
      setPreviewData({
        subject: response.data.subject,
        htmlContent: response.data.htmlContent
      });
      setShowPreview(true);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    }
  };

  const handleTestSend = async () => {
    if (!testEmail || !editingTemplate) return;

    try {
      setSaving(true);
      await axios.post(`/api/admin/email-templates/${editingTemplate.id}/test-send`, {
        toEmail: testEmail,
        mergeData: {},
      });
      toast.success(`Test email sent to ${testEmail}`);
      setShowTestSend(false);
      setTestEmail('');
    } catch (error) {
      toast.error('Failed to send test email');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await axios.put(`/api/admin/email-templates/${template.id}`, {
        isActive: !template.isActive,
      });
      toast.success(`Template ${template.isActive ? 'deactivated' : 'activated'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template status');
    }
  };

  const insertMergeField = (field) => {
    const fieldTag = `{{${field}}}`;
    setFormData((prev) => ({
      ...prev,
      htmlContent: prev.htmlContent + fieldTag,
    }));
    navigator.clipboard.writeText(fieldTag);
    toast.success(`Copied ${fieldTag} to clipboard`);
  };

  // Version Management Functions (A/B Testing Foundation)
  const createVersion = () => {
    if (!versionName.trim()) {
      toast.error('Please enter a version name');
      return;
    }
    const newVersion = {
      id: `v_${Date.now()}`,
      name: versionName.trim(),
      htmlContent: formData.htmlContent,
      blocks: emailBlocks,
      createdAt: new Date().toISOString(),
      isActive: versions.length === 0, // First version is active by default
    };
    setVersions([...versions, newVersion]);
    if (versions.length === 0) {
      setSelectedVersion(newVersion.id);
    }
    setVersionName('');
    setShowVersionDialog(false);
    toast.success(`Version "${newVersion.name}" created`);
  };

  const activateVersion = (versionId) => {
    const version = versions.find(v => v.id === versionId);
    if (version) {
      setFormData(prev => ({ ...prev, htmlContent: version.htmlContent }));
      setEmailBlocks(version.blocks || []);
      setSelectedVersion(versionId);
      setVersions(versions.map(v => ({
        ...v,
        isActive: v.id === versionId
      })));
      toast.success(`Switched to version "${version.name}"`);
    }
  };

  const deleteVersion = (versionId) => {
    if (versions.length <= 1) {
      toast.error('Cannot delete the only version');
      return;
    }
    const newVersions = versions.filter(v => v.id !== versionId);
    setVersions(newVersions);
    if (selectedVersion === versionId && newVersions.length > 0) {
      activateVersion(newVersions[0].id);
    }
    toast.success('Version deleted');
  };

  const handleBlocksChange = (newBlocks) => {
    setEmailBlocks(newBlocks);
  };

  const handleBlockExportHtml = (html) => {
    setFormData(prev => ({ ...prev, htmlContent: html }));
    toast.success('HTML exported from block builder');
  };

  const getDefaultTemplate = () => {
    return `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color: #f5f5f5;">
        <tr>
            <td style="padding: 40px 20px;">
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    <!-- Header -->
                    <tr>
                        <td style="background: linear-gradient(135deg, #24064C 0%, #5B21B6 100%); padding: 40px 40px 30px;">
                            <h1 style="margin: 0; font-size: 28px; font-weight: 700; color: #ffffff; font-family: 'Poppins', sans-serif;">Secret Sauce</h1>
                            <p style="margin: 8px 0 0; font-size: 14px; color: rgba(255,255,255,0.9);">The PATH Programme</p>
                        </td>
                    </tr>
                    
                    <!-- Content -->
                    <tr>
                        <td style="padding: 40px;">
                            <h2 style="margin: 0 0 20px; font-size: 24px; color: #1f2937; font-family: Georgia, 'Times New Roman', serif;">
                                Hi {{first_name}},
                            </h2>
                            
                            <p style="margin: 0 0 20px; font-size: 16px; line-height: 1.6; color: #4b5563;">
                                Your email content goes here.
                            </p>
                            
                            <p style="margin: 24px 0 0; font-size: 16px; line-height: 1.6; color: #4b5563;">
                                Best regards,<br>
                                <strong>The Secret Sauce Team</strong>
                            </p>
                        </td>
                    </tr>
                    
                    <!-- Footer -->
                    <tr>
                        <td style="background-color: #f9fafb; padding: 24px 40px; border-top: 1px solid #e5e7eb;">
                            <p style="margin: 0; font-size: 12px; color: #9ca3af; text-align: center;">
                                © 2024 Secret Sauce. All rights reserved.
                            </p>
                        </td>
                    </tr>
                </table>
            </td>
        </tr>
    </table>
</body>
</html>`;
  };

  return (
      <div data-testid="admin-email-templates">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Email Templates</h1>
            <p className="text-muted-foreground">Manage email templates for automated communications</p>
          </div>
          <Button onClick={handleCreate} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48" data-testid="category-filter">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">
            {templates.length} template(s)
          </span>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-[var(--radius)]">
            <p className="text-muted-foreground mb-4">No email templates found</p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" /> Create First Template
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-card border border-border rounded-[var(--radius)] p-6"
                data-testid={`template-${template.slug}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{template.name}</h3>
                      <Badge variant={template.isActive ? 'default' : 'secondary'}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline">{template.category}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      <code className="bg-muted px-2 py-0.5 rounded">{template.slug}</code>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Subject:</strong> {template.subject}
                    </p>
                    {template.description && (
                      <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                    )}
                    {template.mergeFields?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {template.mergeFields.map((field) => (
                          <span
                            key={field}
                            className="text-xs bg-muted px-2 py-0.5 rounded"
                          >
                            {`{{${field}}}`}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePreview(template)}
                      data-testid={`preview-${template.slug}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(template)}
                      data-testid={`edit-${template.slug}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(template)}
                    >
                      {template.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-${template.slug}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Template Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
            <DialogDescription>
              Create HTML email templates with merge fields for personalization.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="editor" className="mt-4">
            <TabsList>
              <TabsTrigger value="editor">
                <Wand2 className="w-4 h-4 mr-2" />
                Visual Editor
              </TabsTrigger>
              <TabsTrigger value="blocks">
                <Blocks className="w-4 h-4 mr-2" />
                Block Builder
              </TabsTrigger>
              <TabsTrigger value="code">
                <Code className="w-4 h-4 mr-2" />
                HTML Code
              </TabsTrigger>
              <TabsTrigger value="merge-fields">Merge Fields</TabsTrigger>
              <TabsTrigger value="versions">
                <GitBranch className="w-4 h-4 mr-2" />
                Versions
              </TabsTrigger>
            </TabsList>

            <TabsContent value="editor" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Welcome Email"
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
                      })
                    }
                    placeholder="welcome_email"
                    disabled={!!editingTemplate}
                    data-testid="template-slug-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="subject">Subject Line *</Label>
                  <Input
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Welcome to PATH, {{first_name}}!"
                    data-testid="template-subject-input"
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="template-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.filter((c) => c.value !== 'all').map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of when this template is used"
                />
              </div>

              {/* Merge Variables Panel */}
              <div className="border border-border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowVariablesPanel(!showVariablesPanel)}
                  className="w-full flex items-center justify-between p-3 bg-muted/50 hover:bg-muted transition-colors"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Code className="w-4 h-4" />
                    Available Merge Variables
                  </span>
                  <Badge variant="secondary">{MERGE_FIELDS.length} variables</Badge>
                </button>
                {showVariablesPanel && (
                  <div className="p-3 border-t border-border">
                    <Input
                      placeholder="Search variables..."
                      value={variableSearch}
                      onChange={(e) => setVariableSearch(e.target.value)}
                      className="mb-3"
                    />
                    <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {filteredMergeFields.map((field) => (
                        <div
                          key={field.field}
                          className="flex items-center justify-between p-2 bg-muted/30 rounded text-sm cursor-pointer hover:bg-muted transition-colors"
                          onClick={() => {
                            navigator.clipboard.writeText(`{{${field.field}}}`);
                            toast.success(`Copied {{${field.field}}} to clipboard`);
                          }}
                          title="Click to copy"
                        >
                          <div>
                            <code className="text-primary font-medium">{`{{${field.field}}}`}</code>
                            <p className="text-xs text-muted-foreground mt-0.5">{field.description}</p>
                          </div>
                          <Copy className="w-3 h-3 text-muted-foreground" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="mb-2 block">Email Content *</Label>
                <EmailEditor
                  content={formData.htmlContent}
                  onChange={(html) => setFormData({ ...formData, htmlContent: html })}
                  placeholder="Start typing your email content here..."
                />
              </div>
            </TabsContent>

            <TabsContent value="code" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name-code">Template Name *</Label>
                  <Input
                    id="name-code"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Welcome Email"
                  />
                </div>
                <div>
                  <Label htmlFor="slug-code">Slug *</Label>
                  <Input
                    id="slug-code"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
                      })
                    }
                    placeholder="welcome_email"
                    disabled={!!editingTemplate}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="subject-code">Subject Line *</Label>
                  <Input
                    id="subject-code"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Welcome to PATH, {{first_name}}!"
                  />
                </div>
                <div>
                  <Label htmlFor="category-code">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.filter((c) => c.value !== 'all').map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description-code">Description</Label>
                <Input
                  id="description-code"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of when this template is used"
                />
              </div>

              <div>
                <Label htmlFor="htmlContent">Raw HTML Content *</Label>
                <Textarea
                  id="htmlContent"
                  value={formData.htmlContent}
                  onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                  placeholder="<html>...</html>"
                  className="font-mono text-sm min-h-[400px]"
                  data-testid="template-html-input"
                />
              </div>
            </TabsContent>

            <TabsContent value="merge-fields">
              <div className="bg-muted/50 rounded-[var(--radius)] p-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Click a merge field to copy it to clipboard. Use these in your template subject
                  and content.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {MERGE_FIELDS.map((item) => (
                    <button
                      key={item.field}
                      onClick={() => insertMergeField(item.field)}
                      className="flex items-center justify-between p-3 bg-card border border-border rounded-[var(--radius)] hover:bg-muted transition-smooth text-left"
                    >
                      <div>
                        <code className="text-sm font-semibold text-primary">
                          {`{{${item.field}}}`}
                        </code>
                        <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                      </div>
                      <Copy className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Block Builder Tab */}
            <TabsContent value="blocks" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name-blocks">Template Name *</Label>
                  <Input
                    id="name-blocks"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Welcome Email"
                  />
                </div>
                <div>
                  <Label htmlFor="slug-blocks">Slug *</Label>
                  <Input
                    id="slug-blocks"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
                      })
                    }
                    placeholder="welcome_email"
                    disabled={!!editingTemplate}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="subject-blocks">Subject Line *</Label>
                  <Input
                    id="subject-blocks"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Welcome to PATH, {{first_name}}!"
                  />
                </div>
                <div>
                  <Label htmlFor="category-blocks">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.filter((c) => c.value !== 'all').map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Block Builder</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Build your email visually using drag-and-drop blocks
                </p>
                <EmailBlockBuilder
                  blocks={emailBlocks}
                  onChange={handleBlocksChange}
                  onExportHtml={handleBlockExportHtml}
                />
              </div>
            </TabsContent>

            {/* Versions Tab (A/B Testing Foundation) */}
            <TabsContent value="versions" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Template Versions</h3>
                  <p className="text-sm text-muted-foreground">
                    Create multiple versions for A/B testing
                  </p>
                </div>
                <Button onClick={() => setShowVersionDialog(true)} size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Create Version
                </Button>
              </div>

              {versions.length === 0 ? (
                <div className="text-center py-12 bg-muted/30 rounded-lg">
                  <GitBranch className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground mb-2">No versions created yet</p>
                  <p className="text-sm text-muted-foreground">
                    Create versions to test different email designs
                  </p>
                  <Button onClick={() => setShowVersionDialog(true)} className="mt-4">
                    Create First Version
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      className={`p-4 border rounded-lg flex items-center justify-between ${
                        version.id === selectedVersion ? 'border-primary bg-primary/5' : 'border-border'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${version.isActive ? 'bg-emerald-500' : 'bg-muted'}`} />
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {version.name}
                            {version.isActive && (
                              <Badge variant="secondary" className="text-xs">Active</Badge>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Created {new Date(version.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!version.isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => activateVersion(version.id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Activate
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteVersion(version.id)}
                          className="text-destructive hover:text-destructive"
                          disabled={versions.length <= 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Version Create Dialog */}
              <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Version</DialogTitle>
                    <DialogDescription>
                      Save the current template content as a new version for A/B testing
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <Label htmlFor="versionName">Version Name</Label>
                    <Input
                      id="versionName"
                      value={versionName}
                      onChange={(e) => setVersionName(e.target.value)}
                      placeholder="e.g., Version A, Shorter CTA, New Header..."
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowVersionDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={createVersion}>
                      Create Version
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>

          <DialogFooter className="gap-2">
            {editingTemplate && (
              <Button
                variant="outline"
                onClick={() => {
                  setShowTestSend(true);
                }}
              >
                <Send className="w-4 h-4 mr-2" /> Send Test
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog with Device Modes */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              <strong>Subject:</strong> {previewData.subject}
            </DialogDescription>
          </DialogHeader>
          
          {/* Device Toggle */}
          <div className="flex items-center justify-center gap-2 py-2 border-b border-border">
            <span className="text-sm text-muted-foreground mr-2">Preview:</span>
            {[
              { key: 'desktop', label: 'Desktop', width: '100%' },
              { key: 'tablet', label: 'Tablet', width: '768px' },
              { key: 'mobile', label: 'Mobile', width: '375px' }
            ].map(device => (
              <Button
                key={device.key}
                variant={previewDevice === device.key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPreviewDevice(device.key)}
              >
                {device.label}
              </Button>
            ))}
          </div>
          
          {/* Preview Container */}
          <div className="flex justify-center bg-muted/30 p-4 rounded-lg">
            <div 
              className="border border-border rounded-lg overflow-hidden bg-white transition-all duration-300 shadow-lg"
              style={{ 
                width: DEVICE_WIDTHS[previewDevice],
                maxWidth: '100%'
              }}
            >
              <iframe
                srcDoc={previewData.htmlContent}
                title="Email Preview"
                className="w-full min-h-[500px] bg-white"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Send Dialog */}
      <Dialog open={showTestSend} onOpenChange={setShowTestSend}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email with sample merge data to verify the template.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="testEmail">Recipient Email</Label>
            <Input
              id="testEmail"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              data-testid="test-email-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestSend(false)}>
              Cancel
            </Button>
            <Button onClick={handleTestSend} disabled={saving || !testEmail}>
              {saving ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
  );
}
